import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDownCircle,
  ArrowDownRight,
  ArrowUpCircle,
  ArrowUpRight,
  Coins,
  TrendingDown,
  TrendingUp,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageGuide } from '@/components/guide/PageGuide';
import { GuideLabel, PageStatusBanner, SignLegend } from '@/components/guide/FieldGuide';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { api, type PaginatedResponse } from '@/lib/api-client';
import { bangkokDay, bangkokDayEndIso, bangkokDayStartIso } from '@/lib/datetime.js';
import { formatMoneyIn } from '@/components/data-table/columns-helper';
import { signedMoney } from '@/pages/settlements/signed-money';
import { useAuthStore } from '@/stores/auth-store';
import { useAgentCurrencyMap, type AgentCurrencyResolver } from '@/hooks/useAgentCurrency';
import { totalsByCurrency } from '@/pages/finance/BulkApproveWithdrawsButton';
import { cn } from '@/lib/utils';
import { formatCount } from '@/lib/number';
import {
  ChartSkeleton,
  EmptyHint,
  FinanceTrendChart,
  ListSkeleton,
  ProviderList,
  TopMemberList,
  WinlossTrendChart,
  dominantCashCurrency,
  dominantCurrency,
  rollupByDay,
  rollupFinanceByDay,
  rollupByProvider,
  type FinanceDailyRow,
  type MemberReportRow,
  type WinlossDailyRow,
} from './reports/dashboard-widgets.js';
import { KpiCard, Section } from './reports/kpi-card.js';
import { DashboardBalancesSection } from './reports/dashboard-balances.js';
import { subDays } from 'date-fns';

// ---------------------------------------------------------------------------
// Types (mirror apps/report/src/repo/dashboards.ts — money is integer minor
// units serialized as a JS number; format via formatMoney / signedMoney).
// ---------------------------------------------------------------------------

/**
 * Every money figure the dashboard publishes, for ONE denomination. There is no
 * scalar beside the list: the headline used to read whichever block matched a
 * hard-coded 'THB' while the cash flows were plain cross-currency sums, so a
 * JPY-only brand showed zero turnover and a root operator read ¥ added to ฿
 * (#461).
 */
interface DashboardCurrencyRow {
  currency: string;
  deposit: number;
  withdraw: number;
  netFlow: number;
  turnover: number;
  winloss: number;
  /** `null` = no data source yet (`bonusOutAvailable`), never zero. */
  bonusOut: number | null;
  betCount: number;
  activeMembers: number;
}

interface DashboardSummary {
  period: string;
  byCurrency: DashboardCurrencyRow[];
  bonusOutAvailable: boolean;
  registeredMembers: number;
  activeMembers: number;
  depositingMembers: number;
  firstDeposits: number;
}

/**
 * `agentId` is what makes the money line readable: the deposit and withdraw
 * queues carry no currency of their own, and the brand that stamped the request
 * is what names one (CLAUDE.md §2.7).
 */
interface PendingFinanceRow {
  id: string;
  amount: number;
  status: string;
  agentId?: string | null;
}

// ---------------------------------------------------------------------------
// Date range helpers
// ---------------------------------------------------------------------------

/**
 * The dashboard's day windows, on the Bangkok calendar.
 *
 * These used to be the machine's midnight, so an operator abroad opened the
 * shift summary on a "today" that overlapped the platform's business day only
 * partly — the KPI cards and the daily cash report disagreed by however many
 * hours their offset was. Bangkok has no DST, so stepping back whole days from
 * an instant keeps the same wall clock and lands on the right civil day.
 */
function bangkokDayAgo(n: number, now: Date): string {
  return bangkokDay(subDays(now, n).toISOString());
}
function dayStart(day: string, fallback: Date): string {
  return bangkokDayStartIso(day) ?? fallback.toISOString();
}
function dayEnd(day: string, fallback: Date): string {
  return bangkokDayEndIso(day) ?? fallback.toISOString();
}

// The SPA default is refetchOnWindowFocus:false — backoffice data is normally
// driven by explicit invalidation after a mutation. A dashboard is the exception:
// nobody mutates it, and it gets left open on a second monitor, so without this
// it renders whatever was true when the tab was opened and never says otherwise.
// The global 30s staleTime means a focus event only refetches data that has
// actually aged, so flipping between tabs costs nothing.
const REFRESH_ON_RETURN = { refetchOnWindowFocus: true } as const;

// The actionable queue is work waiting on a human: it goes stale the moment a
// colleague approves a withdrawal, and an operator watching it has no reason to
// touch the tab. It polls instead of waiting for a focus event — the same 30s
// cadence the credit row uses, and these are cheap count queries, not the report
// aggregates behind the KPI cards.
const QUEUE_REFRESH = { refetchInterval: 30_000, refetchOnWindowFocus: true } as const;

/**
 * The pending money line, one entry per denomination.
 *
 * This used to be a single `+=` over the page with a comment assuming one
 * currency, which on an L6 scope spanning two brands added ¥1,500 (0-decimal,
 * §2.4) to ฿15.00 and rendered the result with a baht glyph (#532). It shares
 * `totalsByCurrency` with the bulk-approve dialog rather than carrying a second
 * copy of the rule; a row whose brand has not resolved contributes no line
 * (the count beside it is still the true total).
 */
function pendingAmountLines(
  items: PendingFinanceRow[] | undefined,
  currencyOf: AgentCurrencyResolver,
): string[] {
  if (!items || items.length === 0) return [];
  const { totals } = totalsByCurrency(items, currencyOf);
  return totals.map(([currency, amount]) => formatMoneyIn(amount, currency));
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canRead = hasPermission('report:read');
  /**
   * The top-player watch cards page `/reports/members`, which is a member
   * cohort — and since ADR-0003 a cohort is search-first for anyone who cannot
   * reveal a player's personal data (svc-report answers MEMBER-422). There is
   * no search box on a dashboard, so the cards say "no permission" rather than
   * sending a request that would be refused and surfacing it as an error.
   */
  const canBrowseMembers = hasPermission('member:reveal_pii');

  const now = new Date();
  const todayDay = bangkokDayAgo(0, now);
  const yesterdayDay = bangkokDayAgo(1, now);
  const weekStartDay = bangkokDayAgo(6, now);
  const todayFrom = dayStart(todayDay, now);
  const todayTo = dayEnd(todayDay, now);
  const yFrom = dayStart(yesterdayDay, now);
  const yTo = dayEnd(yesterdayDay, now);
  const weekFrom = dayStart(weekStartDay, now);
  const weekTo = dayEnd(todayDay, now);

  const todayQ = useQuery<DashboardSummary>({
    queryKey: ['dashboard-today', todayFrom, todayTo],
    queryFn: () =>
      api.get<DashboardSummary>('/api/v1/reports/dashboard', {
        params: { from: todayFrom, to: todayTo },
      }),
    enabled: canRead,
    ...REFRESH_ON_RETURN,
  });

  const yesterdayQ = useQuery<DashboardSummary>({
    queryKey: ['dashboard-yesterday', yFrom, yTo],
    queryFn: () =>
      api.get<DashboardSummary>('/api/v1/reports/dashboard', {
        params: { from: yFrom, to: yTo },
      }),
    enabled: canRead,
    // Yesterday still moves: bets settle after midnight, so the delta chips
    // would compare today against a frozen baseline without this.
    ...REFRESH_ON_RETURN,
  });

  const financeQ = useQuery<{ items: FinanceDailyRow[] }>({
    queryKey: ['dashboard-finance-daily', weekFrom, weekTo],
    queryFn: () =>
      api.get<{ items: FinanceDailyRow[] }>('/api/v1/reports/finance-daily', {
        params: { from: weekFrom, to: weekTo },
      }),
    enabled: canRead,
    ...REFRESH_ON_RETURN,
  });

  const winlossQ = useQuery<{ items: WinlossDailyRow[] }>({
    queryKey: ['dashboard-winloss-daily', weekFrom, weekTo],
    queryFn: () =>
      api.get<{ items: WinlossDailyRow[] }>('/api/v1/reports/winloss-daily', {
        params: { from: weekFrom, to: weekTo },
      }),
    enabled: canRead,
    ...REFRESH_ON_RETURN,
  });

  // Top players today — re-use /reports/members (carries USERNAME, so the watch
  // lists never show raw member UUIDs). One fetch, sorted three ways on client.
  const membersQ = useQuery<PaginatedResponse<MemberReportRow>>({
    queryKey: ['dashboard-members', todayFrom, todayTo],
    queryFn: () =>
      api.listPaginated<MemberReportRow>('/api/v1/reports/members', {
        from: todayFrom,
        to: todayTo,
        page: 1,
        pageSize: 50,
      }),
    enabled: canRead && canBrowseMembers,
    ...REFRESH_ON_RETURN,
  });

  const providersQ = useQuery<PaginatedResponse<{ id: string; name: string; displayName: string }>>({
    queryKey: ['dashboard-provider-names'],
    staleTime: 5 * 60_000,
    queryFn: () =>
      api.listPaginated<{ id: string; name: string; displayName: string }>('/api/v1/providers', {
        page: 1,
        pageSize: 200,
      }),
    enabled: canRead,
  });
  const providerNames = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const p of providersQ.data?.items ?? []) m.set(p.id, p.displayName || p.name);
    return m;
  }, [providersQ.data]);

  // Actionable queue — pending finance + risk. Fetch a page of pending rows so
  // we can show both the count (total) AND the summed ฿ amount, not just a count.
  const pendingDepositsQ = useQuery<PaginatedResponse<PendingFinanceRow>>({
    queryKey: ['dashboard-pending-deposits'],
    queryFn: () =>
      api.listPaginated<PendingFinanceRow>('/api/v1/deposits', {
        status: 'pending',
        page: 1,
        pageSize: 100,
      }),
    ...QUEUE_REFRESH,
  });
  const pendingWithdrawsQ = useQuery<PaginatedResponse<PendingFinanceRow>>({
    queryKey: ['dashboard-pending-withdraws'],
    queryFn: () =>
      api.listPaginated<PendingFinanceRow>('/api/v1/withdraws', {
        status: 'pending',
        page: 1,
        pageSize: 100,
      }),
    ...QUEUE_REFRESH,
  });
  const unackAlertsQ = useQuery<PaginatedResponse<unknown>>({
    queryKey: ['dashboard-unack-alerts'],
    queryFn: () =>
      api.listPaginated('/api/v1/alerts', { acknowledged: 'false', page: 1, pageSize: 1 }),
    ...QUEUE_REFRESH,
  });
  const multiAccountsQ = useQuery<PaginatedResponse<unknown>>({
    queryKey: ['dashboard-multi-accounts'],
    queryFn: () =>
      api.listPaginated('/api/v1/multi-accounts', { page: 1, pageSize: 1 }),
    ...QUEUE_REFRESH,
  });

  const today = todayQ.data;

  // Win/loss week → trend + by-provider, scoped to the dominant currency.
  const winlossRows = winlossQ.data?.items ?? [];
  const winlossCurrency = React.useMemo(() => dominantCurrency(winlossRows), [winlossRows]);
  // House perspective: rollup winloss is player-view (negative = house won), so
  // negate for display — consistent with the houseWinloss headline. Rollups stay raw.
  const providerRollup = React.useMemo(
    () => rollupByProvider(winlossRows, winlossCurrency).slice(0, 6).map((r) => ({ ...r, winloss: -r.winloss })),
    [winlossRows, winlossCurrency],
  );
  const winlossByDay = React.useMemo(
    () => rollupByDay(winlossRows, winlossCurrency).map((p) => ({ ...p, winloss: -p.winloss })),
    [winlossRows, winlossCurrency],
  );

  // Deposit/withdraw bars for ONE denomination. Two currencies on one axis are
  // not comparable heights, and the feed is one row per (currency, day) (#532).
  const financeRows = React.useMemo(() => financeQ.data?.items ?? [], [financeQ.data]);
  const financeCurrency = React.useMemo(() => dominantCashCurrency(financeRows), [financeRows]);
  const financeByDay = React.useMemo(
    () => rollupFinanceByDay(financeRows, financeCurrency),
    [financeRows, financeCurrency],
  );

  const members = React.useMemo(() => membersQ.data?.items ?? [], [membersQ.data]);
  const topTurnover = React.useMemo(
    () => [...members].sort((a, b) => b.turnover - a.turnover).slice(0, 8),
    [members],
  );
  // Player perspective: the member row `winloss` = payout − stake, so a player who
  // WON has positive winloss, one who LOST has negative. Labels read "players who
  // won / lost the most today".
  const topWinners = React.useMemo(
    () => [...members].filter((m) => m.winloss > 0).sort((a, b) => b.winloss - a.winloss).slice(0, 8),
    [members],
  );
  const topLosers = React.useMemo(
    () => [...members].filter((m) => m.winloss < 0).sort((a, b) => a.winloss - b.winloss).slice(0, 8),
    [members],
  );

  const currencyOf = useAgentCurrencyMap();
  const depAmounts = React.useMemo(
    () => pendingAmountLines(pendingDepositsQ.data?.items, currencyOf),
    [pendingDepositsQ.data, currencyOf],
  );
  const wdAmounts = React.useMemo(
    () => pendingAmountLines(pendingWithdrawsQ.data?.items, currencyOf),
    [pendingWithdrawsQ.data, currencyOf],
  );
  const riskCount =
    (unackAlertsQ.data?.total ?? 0) + (multiAccountsQ.data?.total ?? 0);

  // A `null` block is "the range has not answered yet": the sections still
  // render, with em-dashes rather than a zero that would read as a real total.
  const moneyBlocks: (DashboardCurrencyRow | null)[] =
    today && today.byCurrency.length > 0 ? today.byCurrency : [null];
  const multiCurrency = moneyBlocks.length > 1;
  // Yesterday's block for the SAME denomination. A delta against another
  // currency's figure is not a comparison at all.
  const yesterdayByCurrency = React.useMemo(() => {
    const m = new Map<string, DashboardCurrencyRow>();
    for (const row of yesterdayQ.data?.byCurrency ?? []) m.set(row.currency, row);
    return m;
  }, [yesterdayQ.data]);
  const sectionTitle = (key: string, row: DashboardCurrencyRow | null) =>
    multiCurrency && row ? `${t(key)} · ${row.currency}` : t(key);

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('nav.dashboard')}
        description={user ? `${user.displayName ?? user.username}` : t('dashboard.description')}
        actions={<PageGuide pageId="dashboard.home" />}
      />

      {/* The queue tiles 403 into a rendered 0 and their money line stops at the
          first page of pending items — both are silent, so the banner is the
          only thing that says so. */}
      <PageStatusBanner pageId="dashboard.home" />

      <DashboardBalancesSection />

      {/* ---- Headline KPIs (today, vs yesterday), one block per denomination ---- */}
      {moneyBlocks.map((row, i) => {
        const prev = row ? yesterdayByCurrency.get(row.currency) : undefined;
        // HOUSE perspective: the row's `winloss` is player-view (payout − stake),
        // so negate — players' net loss is the house's profit (positive/green).
        const houseWinloss = row
          ? signedMoney(-row.winloss, row.currency)
          : { text: '—', negative: false, zero: true };
        const houseTone: 'positive' | 'negative' | undefined = !row
          ? undefined
          : houseWinloss.negative
            ? 'negative'
            : houseWinloss.zero
              ? undefined
              : 'positive';
        return (
          <React.Fragment key={row?.currency ?? `pending-${i}`}>
            <Section title={sectionTitle('dashboard.sections.headline', row)} testId="dashboard-headline">
              <KpiCard
                icon={<Coins className="h-4 w-4 text-emerald-500" />}
                label={
                  <GuideLabel pageId="dashboard.home" fieldKey="houseWinloss">
                    {t('dashboard.houseWinloss')}
                  </GuideLabel>
                }
                value={houseWinloss.text}
                tone={houseTone}
                delta={
                  <DeltaLabel
                    curr={row ? -row.winloss : undefined}
                    prev={prev ? -prev.winloss : undefined}
                    tLabel={t('dashboard.vsYesterday')}
                  />
                }
              />
              <KpiCard
                icon={<TrendingUp className="h-4 w-4 text-sky-500" />}
                label={
                  <GuideLabel pageId="dashboard.home" fieldKey="turnoverToday">
                    {t('dashboard.turnoverToday')}
                  </GuideLabel>
                }
                value={formatMoneyIn(row?.turnover ?? 0, row?.currency ?? null)}
                delta={
                  <DeltaLabel
                    curr={row?.turnover}
                    prev={prev?.turnover}
                    tLabel={t('dashboard.vsYesterday')}
                  />
                }
              />
              <KpiCard
                icon={<Wallet className="h-4 w-4 text-sky-500" />}
                label={
                  <GuideLabel pageId="dashboard.home" fieldKey="netInflowToday">
                    {t('dashboard.netInflowToday')}
                  </GuideLabel>
                }
                value={formatMoneyIn(row?.netFlow ?? 0, row?.currency ?? null)}
                tone={row ? (row.netFlow >= 0 ? 'positive' : 'negative') : undefined}
                delta={
                  <DeltaLabel
                    curr={row?.netFlow}
                    prev={prev?.netFlow}
                    tLabel={t('dashboard.vsYesterday')}
                  />
                }
              />
              <KpiCard
                icon={<Users className="h-4 w-4 text-violet-500" />}
                label={
                  <GuideLabel pageId="dashboard.home" fieldKey="activeMembers">
                    {t('dashboard.activeMembers')}
                  </GuideLabel>
                }
                value={row ? formatCount(row.activeMembers, i18n.language) : '—'}
                delta={
                  <DeltaLabel
                    curr={row?.activeMembers}
                    prev={prev?.activeMembers}
                    tLabel={t('dashboard.vsYesterday')}
                  />
                }
              />
            </Section>

            <Section title={sectionTitle('dashboard.sections.flow', row)} testId="dashboard-flow">
              <KpiCard
                icon={<ArrowDownCircle className="h-4 w-4 text-emerald-500" />}
                label={
                  <GuideLabel pageId="dashboard.home" fieldKey="deposits">
                    {t('dashboard.deposits')}
                  </GuideLabel>
                }
                value={formatMoneyIn(row?.deposit ?? 0, row?.currency ?? null)}
                delta={
                  <DeltaLabel
                    curr={row?.deposit}
                    prev={prev?.deposit}
                    tLabel={t('dashboard.vsYesterday')}
                  />
                }
              />
              <KpiCard
                icon={<ArrowUpCircle className="h-4 w-4 text-amber-500" />}
                label={
                  <GuideLabel pageId="dashboard.home" fieldKey="withdraws">
                    {t('dashboard.withdraws')}
                  </GuideLabel>
                }
                value={formatMoneyIn(row?.withdraw ?? 0, row?.currency ?? null)}
                delta={
                  <DeltaLabel
                    curr={row?.withdraw}
                    prev={prev?.withdraw}
                    tLabel={t('dashboard.vsYesterday')}
                    invert
                  />
                }
              />
            </Section>
          </React.Fragment>
        );
      })}

      {/* Always visible, never behind the guide button: this screen shows the
          house view up here and the player view in the watch lists below, so a
          reader who does not know which half they are in reads the colour
          backwards. */}
      <SignLegend pageId="dashboard.home" fieldKey="houseWinloss" />

      {/* Head counts are scope-wide and currency-free: people are commensurable
          across brands in a way minor units are not. */}
      <Section title={t('reports.dashboardTab.sections.members')} testId="dashboard-members">
        <KpiCard
          icon={<UserPlus className="h-4 w-4 text-violet-500" />}
          label={
            <GuideLabel pageId="dashboard.home" fieldKey="newMembers">
              {t('dashboard.newMembersToday')}
            </GuideLabel>
          }
          value={today ? formatCount(today.registeredMembers, i18n.language) : '—'}
          delta={
            <DeltaLabel
              curr={today?.registeredMembers}
              prev={yesterdayQ.data?.registeredMembers}
              tLabel={t('dashboard.vsYesterday')}
            />
          }
        />
        <KpiCard
          icon={<UserPlus className="h-4 w-4 text-pink-500" />}
          label={
            <GuideLabel pageId="dashboard.home" fieldKey="firstDeposits">
              {t('reports.firstDeposits')}
            </GuideLabel>
          }
          value={today ? formatCount(today.firstDeposits, i18n.language) : '—'}
          delta={
            <DeltaLabel
              curr={today?.firstDeposits}
              prev={yesterdayQ.data?.firstDeposits}
              tLabel={t('dashboard.vsYesterday')}
            />
          }
        />
      </Section>

      {/* ---- Trends ---- */}
      {canRead ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card className="lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                {t('dashboard.depositWithdrawWeek')}
                {financeCurrency ? (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    {financeCurrency}
                  </span>
                ) : null}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {financeQ.isLoading ? <ChartSkeleton /> : <FinanceTrendChart rows={financeByDay} t={t} />}
            </CardContent>
          </Card>

          <Card className="lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                <GuideLabel pageId="dashboard.home" fieldKey="winlossWeek">
                  {t('dashboard.winlossWeek')}
                </GuideLabel>
                {winlossRows.length > 0 ? (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    {winlossCurrency}
                  </span>
                ) : null}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {winlossQ.isLoading ? (
                <ChartSkeleton />
              ) : (
                <WinlossTrendChart points={winlossByDay} currency={winlossCurrency} t={t} />
              )}
            </CardContent>
          </Card>

          <Card className="lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                <GuideLabel pageId="dashboard.home" fieldKey="winlossByProvider">
                  {t('reports.dashboardTab.winlossByProvider')}
                </GuideLabel>
                {winlossRows.length > 0 ? (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    {winlossCurrency}
                  </span>
                ) : null}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {winlossQ.isLoading ? (
                <ListSkeleton />
              ) : providerRollup.length === 0 ? (
                <EmptyHint label={t('common.noData')} />
              ) : (
                <ProviderList rows={providerRollup} currency={winlossCurrency} names={providerNames} />
              )}
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* ---- Player watch (risk-useful) ---- */}
      {canRead ? (
        <div className="space-y-2">
          {/* These three cards flip the convention used everywhere above: a
              plus here is the PLAYER winning. Legend repeated rather than
              inherited, because the two halves scroll apart. */}
          <SignLegend pageId="dashboard.home" fieldKey="playerWinloss" />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <WatchCard
              title={
                <GuideLabel pageId="dashboard.home" fieldKey="topTurnover">
                  {t('dashboard.topTurnover')}
                </GuideLabel>
              }
              loading={membersQ.isLoading}
              empty={topTurnover.length === 0}
              emptyLabel={canBrowseMembers ? t('common.noData') : t('common.noPermission')}
            >
              <TopMemberList rows={topTurnover} t={t} metric="turnover" />
            </WatchCard>
            <WatchCard
              title={
                <GuideLabel pageId="dashboard.home" fieldKey="playerWinloss">
                  {t('dashboard.topWinners')}
                </GuideLabel>
              }
              icon={<TrendingUp className="h-4 w-4 text-emerald-500" />}
              loading={membersQ.isLoading}
              empty={topWinners.length === 0}
              emptyLabel={canBrowseMembers ? t('common.noData') : t('common.noPermission')}
            >
              {/* Player view: winners have positive winloss → signedMoney renders green. */}
              <TopMemberList rows={topWinners} t={t} metric="winloss" />
            </WatchCard>
            <WatchCard
              title={
                <GuideLabel pageId="dashboard.home" fieldKey="playerWinloss">
                  {t('dashboard.topLosers')}
                </GuideLabel>
              }
              icon={<TrendingDown className="h-4 w-4 text-destructive" />}
              loading={membersQ.isLoading}
              empty={topLosers.length === 0}
              emptyLabel={canBrowseMembers ? t('common.noData') : t('common.noPermission')}
            >
              <TopMemberList rows={topLosers} t={t} metric="winloss" />
            </WatchCard>
          </div>
        </div>
      ) : null}

      {/* ---- Actionable queue (clickable → relevant page) ---- */}
      <Section title={t('dashboard.sections.queue')} testId="dashboard-queue">
        <QueueCard
          to="/finance"
          testId="queue-pending-deposits"
          icon={<ArrowDownCircle className="h-4 w-4 text-emerald-500" />}
          label={
            <GuideLabel pageId="dashboard.home" fieldKey="pendingDeposits">
              {t('dashboard.pendingDeposits')}
            </GuideLabel>
          }
          count={pendingDepositsQ.data?.total}
          amounts={depAmounts}
          loading={pendingDepositsQ.isLoading}
        />
        <QueueCard
          to="/finance"
          testId="queue-pending-withdraws"
          icon={<ArrowUpCircle className="h-4 w-4 text-amber-500" />}
          label={
            <GuideLabel pageId="dashboard.home" fieldKey="pendingWithdraws">
              {t('dashboard.pendingWithdraws')}
            </GuideLabel>
          }
          count={pendingWithdrawsQ.data?.total}
          amounts={wdAmounts}
          loading={pendingWithdrawsQ.isLoading}
        />
        <QueueCard
          to="/risk"
          testId="queue-risk"
          icon={<AlertTriangle className="h-4 w-4 text-destructive" />}
          label={
            <GuideLabel pageId="dashboard.home" fieldKey="riskQueue">
              {t('dashboard.riskQueue')}
            </GuideLabel>
          }
          count={riskCount}
          subLabel={t('dashboard.riskQueueHint')}
          loading={unackAlertsQ.isLoading || multiAccountsQ.isLoading}
        />
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page-local primitives
// ---------------------------------------------------------------------------

function computeDelta(curr: number | undefined, prev: number | undefined): number | null {
  if (curr === undefined || prev === undefined) return null;
  if (prev === 0) return curr === 0 ? 0 : 100;
  return ((curr - prev) / Math.abs(prev)) * 100;
}

/** Compact "+12.3% vs yesterday" delta chip. `invert` flips the colour for
 * metrics where growth is bad (withdraws). */
function DeltaLabel({
  curr,
  prev,
  tLabel,
  invert,
}: {
  curr: number | undefined;
  prev: number | undefined;
  tLabel: string;
  invert?: boolean;
}) {
  const delta = computeDelta(curr, prev);
  if (delta === null) return <span className="text-[11px] text-muted-foreground">{tLabel}</span>;
  const up = delta > 0;
  const down = delta < 0;
  const positive = invert ? down : up;
  const negative = invert ? up : down;
  const sign = delta > 0 ? '+' : '';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-[11px] font-medium',
        positive && 'text-emerald-600 dark:text-emerald-400',
        negative && 'text-destructive',
        !positive && !negative && 'text-muted-foreground',
      )}
    >
      {up ? <ArrowUpRight className="h-3 w-3" /> : down ? <ArrowDownRight className="h-3 w-3" /> : null}
      {sign}
      {delta.toFixed(1)}% {tLabel}
    </span>
  );
}

function WatchCard({
  title,
  icon,
  loading,
  empty,
  emptyLabel,
  children,
}: {
  /** A node, so the title can carry its own info icon without being clipped. */
  title: React.ReactNode;
  icon?: React.ReactNode;
  loading: boolean;
  empty: boolean;
  emptyLabel: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-1.5 text-sm">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? <ListSkeleton /> : empty ? <EmptyHint label={emptyLabel} /> : children}
      </CardContent>
    </Card>
  );
}

function QueueCard({
  to,
  testId,
  icon,
  label,
  subLabel,
  count,
  amounts,
  loading,
}: {
  to: string;
  testId: string;
  icon: React.ReactNode;
  /** A node, so the label can carry its own info icon. */
  label: React.ReactNode;
  subLabel?: string;
  count: number | undefined;
  /**
   * One line per denomination, never their sum: an operator's scope can span
   * brands on different currencies, and adding their minor units produces a
   * figure that is not an amount of anything (#532).
   */
  amounts?: string[];
  loading: boolean;
}) {
  const { i18n } = useTranslation();
  return (
    <Link to={to} className="block" data-testid={testId}>
      <Card className="p-3 transition-colors hover:bg-accent/50">
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 items-center gap-2">
            {icon}
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{label}</div>
              {amounts && amounts.length > 0 ? (
                amounts.map((amount) => (
                  <div
                    key={amount}
                    data-testid="queue-amount"
                    className="truncate text-xs tabular-nums text-muted-foreground"
                  >
                    {amount}
                  </div>
                ))
              ) : subLabel ? (
                <div className="truncate text-xs text-muted-foreground">{subLabel}</div>
              ) : null}
            </div>
          </div>
          <span data-testid="queue-count" className="shrink-0 text-2xl font-semibold tabular-nums">
            {loading ? '—' : formatCount(count ?? 0, i18n.language)}
          </span>
        </div>
      </Card>
    </Link>
  );
}
